import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2, Wand2, Check } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { api, errMsg } from "../lib/api";
import { SEV } from "../lib/summary";

const MAX_MS = 60000;

export default function VoiceCapture({ category, item, mode, auto, onApply }) {
  const [rec, setRec] = useState(false);
  const [busy, setBusy] = useState("");
  const [text, setText] = useState("");
  const [sug, setSug] = useState(null);
  const mr = useRef(null);
  const timer = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const m = new MediaRecorder(stream);
      m.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      m.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = m.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type });
        const fd = new FormData();
        fd.append("audio", blob, `note.${type.includes("mp4") ? "mp4" : "webm"}`);
        setBusy("transcribe");
        try {
          const { data } = await api.post("/ai/transcribe", fd);
          setText(data.text);
          if (data.text) structure(data.text);
        } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
      };
      m.start();
      mr.current = m;
      setRec(true);
      setSug(null);
      timer.current = setTimeout(stop, MAX_MS);
    } catch {
      toast.error("Microphone not available. Allow mic access or type the note below.");
    }
  };
  const stop = () => {
    clearTimeout(timer.current);
    if (mr.current?.state === "recording") mr.current.stop();
    setRec(false);
  };
  const structure = async (t = text) => {
    if (!t.trim()) { toast.error("Record or type a note first"); return; }
    setBusy("structure");
    try { const { data } = await api.post("/ai/voice-structure", { transcript: t, category, item, mode }); setSug(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  useEffect(() => { if (auto) start(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="voice-capture">
      <div className="flex items-center gap-2">
        <button type="button" onClick={rec ? stop : start} disabled={!!busy} data-testid={rec ? "voice-stop-button" : "voice-record-button"}
          className={`tap flex h-14 flex-1 items-center justify-center gap-2 rounded-md text-sm font-bold text-white ${rec ? "animate-pulse bg-rose-600" : "bg-slate-900"}`}>
          {rec ? <><Square className="h-4 w-4" />STOP · converting after</> : busy === "transcribe" ? <><Loader2 className="h-4 w-4 animate-spin" />TRANSCRIBING…</> : <><Mic className="h-5 w-5 text-teal-300" />VOICE NOTE</>}
        </button>
      </div>
      <Textarea rows={2} className="mt-2 bg-white" placeholder="Speak, e.g. “hollow sound in two floor tiles near balcony door, about 600 mm”" value={text} onChange={(e) => setText(e.target.value)} data-testid="voice-transcript-input" />
      <button type="button" onClick={() => structure()} disabled={!!busy || !text.trim()} className="tap mt-2 flex h-10 w-full items-center justify-center gap-2 rounded-md border border-teal-600 bg-white text-sm font-bold text-teal-800 disabled:opacity-40" data-testid="voice-convert-button">
        {busy === "structure" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}CONVERT TO STANDARD REMARK
      </button>
      {sug && (
        <div className="mt-3 rounded-md border border-teal-300 bg-teal-50 p-3 text-sm" data-testid="voice-suggestion-card">
          <div className="font-mono text-[10px] font-bold tracking-widest text-teal-800">FROM VOICE · {sug.mode === "ai" ? "REAL AI" : "KEYWORD MATCH"}{sug.fallback ? " (FALLBACK)" : ""}</div>
          <div className="mt-1"><b>{sug.name}</b> <span className="text-slate-500">· {sug.category} · {SEV[sug.severity]?.label}</span></div>
          <div className="mt-1">“{sug.remark}”</div>
          <div className="mt-1 text-slate-600">“{sug.recommendation}”</div>
          {sug.measurement && <div className="mt-1 font-mono text-xs">Measurement: {sug.measurement}</div>}
          <button type="button" onClick={() => { onApply({ ...sug, transcript: text }); setSug(null); }} className="tap mt-2 flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-teal-700 font-semibold text-white" data-testid="voice-approve-button"><Check className="h-4 w-4" />Approve & edit</button>
        </div>
      )}
    </div>
  );
}
